import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Collapse } from "react-bootstrap";
import { shallowEqual, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useSubheader } from "../../../../_metronic/layout";
import { BadgeCard } from "../Components/BadgeCard";
import { BadgeFilters } from "../Components/BadgeFilters";
import { BadgeSheet } from "../Components/BadgeSheet";
import { CheckInTabs } from "../Components/CheckInTabs";
import { useCheckInContext } from "../Components/CheckInDataContext";
import {
  batchDays,
  describeBadgeError,
  fetchBadges,
  fetchBadgesForBatches,
  formatBatchLabel,
  sortWeekdays,
} from "../Components/badgesCrud";
import { useBadgePrintMode, useSoloPrint } from "../Components/printing";

const DEFAULT_FILTERS = { active: "Active", day: "", program: "", delivery: "" };
const NO_FILTERS = { active: "", day: "", program: "", delivery: "" };

/**
 * The class dropdown's value for "every class on the filtered day".
 *
 * A string sentinel, deliberately not a number and not "": it can never
 * collide with a batch id, and every place that turns a dropdown value into an
 * id has to decide what to do with it rather than coercing it to NaN.
 *
 * It is offered ONLY while the Day filter names a day. On "All" it would mean
 * the branch's entire roster — nobody asked for that, and since fetching
 * badges MINTS a durable code for every student who lacks one, an accidental
 * press would be a very large write.
 */
const ALL_CLASSES = "all-classes";

/**
 * `?batch_id=` from the Classes list's "Print badges" row action.
 *
 * Anything that is not a positive integer — absent, blank, "12abc", "-3" — is
 * ignored outright and the screen falls back to the ordinary empty picker.
 * A hand-edited URL must never reach the API as batch NaN or hang the screen
 * on a fetch that can only fail.
 */
function parseLinkedBatchId(search) {
  const raw = new URLSearchParams(search).get("batch_id");
  if (raw === null || raw.trim() === "") return null;
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : null;
}

/**
 * Keying on the selected branch remounts the printer whenever it changes, so
 * batches, badges, ticks and filters all reset to a clean page — no effect
 * choreography, and no chance of printing one branch's sheet under another
 * branch's name.
 *
 * Branch context needs no reconciliation on a deep link: Batches and Check-in
 * read the *same* selection — `auth.branches.currentBranch`, the subheader
 * picker (see BatchList.js and CheckInDataContext.js, both of which fetch
 * `api/batches/branch/<currentBranch.branch_id>`). Check-in never had a branch
 * of its own, so arriving from a Classes row cannot change which branch is
 * current, and the header cannot end up naming a different one.
 */
export function BadgePrintList() {
  const { BRANCH } = useCheckInContext();
  const { search } = useLocation();
  const linkedBatchId = useMemo(() => parseLinkedBatchId(search), [search]);
  return (
    <BadgePrinter key={String(BRANCH.branchId)} linkedBatchId={linkedBatchId} />
  );
}

function BadgePrinter({ linkedBatchId }) {
  const suhbeader = useSubheader();
  suhbeader.setTitle("Print Student Badges");

  const { BRANCH, BATCH, states } = useCheckInContext();
  const { batches } = BATCH;
  // Attached explicitly to the badges call — see badgesCrud's header. The
  // auto-fetch below runs on mount, which is exactly where axios.defaults has
  // not been populated yet.
  const authToken = useSelector((state) => state.auth.authToken, shallowEqual);

  const deepLinked = linkedBatchId !== null;

  const [batchId, setBatchId] = useState(deepLinked ? linkedBatchId : "");
  // Arriving on a named class, the picker is a fallback, not the front door:
  // it starts collapsed behind "Choose a different class". Entered cold, it
  // is open from the first paint, exactly as before.
  const [pickerOpen, setPickerOpen] = useState(!deepLinked);
  const [badges, setBadges] = useState([]);
  // user_ids included in the print sheet. Every fetch selects everyone — the
  // common case is the whole class; narrowing is the exception, not a step.
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // What a whole-day sheet was actually built from — classes fetched,
  // duplicates dropped. Kept in state rather than derived, because it
  // describes the sheet on screen, not whatever the filters say now.
  const [mergeNote, setMergeNote] = useState("");
  // Mirrors the Classes list's filters: client-side over the one branch fetch,
  // Active by default, applied on change. A deep link starts them all on "All"
  // instead: the Active default would otherwise suspend (see effectiveBatchId)
  // the very class we were sent to print, if that class happens to be
  // inactive — and the Classes list will happily show inactive rows.
  const [filters, setFilters] = useState(
    deepLinked ? NO_FILTERS : DEFAULT_FILTERS
  );
  // When set, the print sheet is exactly this one student, whatever the ticks
  // say — the per-card Print button. Cleared as soon as the dialog closes.
  const [soloPrintId, setSoloPrintId] = useState(null);

  // Held for as long as this screen is mounted, so Ctrl+P prints the sheet too.
  useBadgePrintMode(true);
  useSoloPrint(soloPrintId, () => setSoloPrintId(null));

  const toggleSelected = useCallback((userId) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }, []);

  /* Dropdown options come from the batches themselves, so each filter only
     ever offers values that exist at this branch. */
  const filterOptions = useMemo(() => {
    const days = new Set();
    const programs = new Set();
    const deliveries = new Set();
    batches.forEach((batch) => {
      batchDays(batch).forEach((day) => days.add(day));
      if (batch.program_name) programs.add(batch.program_name);
      if (batch.delivery_type_value) deliveries.add(batch.delivery_type_value);
    });
    return {
      days: sortWeekdays([...days]),
      programs: [...programs].sort(),
      deliveries: [...deliveries].sort(),
    };
  }, [batches]);

  const filteredBatches = useMemo(
    () =>
      batches.filter(
        (batch) =>
          (filters.active === "" ||
            (batch.active_value || "") === filters.active) &&
          // Day matches any of a multi-day batch's days — strict equality
          // misses "Monday,Wednesday".
          (filters.day === "" || batchDays(batch).includes(filters.day)) &&
          (filters.program === "" || batch.program_name === filters.program) &&
          (filters.delivery === "" ||
            batch.delivery_type_value === filters.delivery)
      ),
    [batches, filters]
  );

  // "All classes on this day" is offered only while the Day filter names a
  // day, and only when there is more than one class to merge — with a single
  // match it would just be that class under a longer name.
  const allClassesOffered = filters.day !== "" && filteredBatches.length > 1;

  // A filter that hides the chosen class suspends the choice (and the ticks
  // over its fetched sheet); widening the filter brings both straight back. A
  // manually typed batch id is never in the list at all, so filters leave it
  // alone.
  //
  // Clearing the Day filter suspends an armed all-classes selection by the
  // same rule: the option is no longer offered, so the choice made against it
  // cannot stand, and it must never silently widen to the whole branch.
  const effectiveBatchId = useMemo(() => {
    if (batchId === ALL_CLASSES) return allClassesOffered ? ALL_CLASSES : "";
    if (batchId === "") return "";
    const known = batches.some((batch) => batch.batch_id === batchId);
    const visible = filteredBatches.some((batch) => batch.batch_id === batchId);
    return known && !visible ? "" : batchId;
  }, [batchId, batches, filteredBatches, allClassesOffered]);

  const allClassesArmed = effectiveBatchId === ALL_CLASSES;

  /*
    Active classes first, then by start time. A branch can carry years of
    retired batches, and a picker that opens on those is a picker nobody trusts.
  */
  const batchOptions = useMemo(
    () =>
      [...filteredBatches]
        .sort((a, b) => {
          const activeA = a.active_value === "Active" ? 0 : 1;
          const activeB = b.active_value === "Active" ? 0 : 1;
          if (activeA !== activeB) return activeA - activeB;
          return (a.start_time || "").localeCompare(b.start_time || "");
        })
        .map((batch) => {
          // No enrolment count in this label. `batch.enrolled_student_count`
          // is a stale denormalised column: it reads 0 on 501 of 3579 batches
          // that actually hold students, always under-reporting, so a count
          // here said "0 students" for classes that print a full sheet. A
          // wrong number is worse than no number — it tells staff not to
          // bother fetching. Restore this only against a real COUNT.
          return {
            id: batch.batch_id,
            label: `${formatBatchLabel(batch)}${
              batch.active_value === "Active" ? "" : "  (inactive)"
            }`,
          };
        }),
    [filteredBatches]
  );

  // Exactly the classes the dropdown is offering right now, in the order it
  // offers them. "All classes" therefore honours every active filter, not just
  // Day: with Program set to Robotics it is every Robotics class on that day,
  // which is the set on screen rather than a second, wider set nobody chose.
  const allClassesIds = useMemo(
    () => batchOptions.map((option) => option.id),
    [batchOptions]
  );

  const allClassesLabel = useMemo(() => {
    if (!allClassesOffered) return "";
    // Names the day, so the option is readable without looking back up at the
    // filter row to remember which day is set.
    const narrowed =
      filters.active !== "" || filters.program !== "" || filters.delivery !== "";
    return `All ${allClassesIds.length} ${filters.day} classes${
      narrowed ? " matching these filters" : ""
    }`;
  }, [allClassesOffered, allClassesIds.length, filters]);

  /*
    What pressing the button would cost, worked out BEFORE anything is sent.

    `batch.students` rides along on the branch fetch this screen already made
    (`student_batch` rows for the branch), so this costs no request and writes
    nothing. It is an estimate and is labelled as one on screen: that query
    takes `active_id = 1` while POST /badges takes `active_id IN (1, 6)`, so it
    can only under-count, never over-count. `enrolled_student_count` is not
    used — it is the stale denormalised column that reads 0 on classes holding
    students (see batchOptions above).
  */
  const allClassesPreview = useMemo(() => {
    if (!allClassesOffered) return null;
    const students = new Set();
    let enrolments = 0;
    let classesWithNoRoster = 0;
    filteredBatches.forEach((batch) => {
      const rows = Array.isArray(batch.students) ? batch.students : [];
      if (rows.length === 0) classesWithNoRoster += 1;
      rows.forEach((row) => {
        if (!row || !row.student_user_id) return;
        enrolments += 1;
        students.add(row.student_user_id);
      });
    });
    return {
      enrolments,
      students: students.size,
      duplicates: enrolments - students.size,
      classesWithNoRoster,
    };
  }, [allClassesOffered, filteredBatches]);

  const labelForBatchId = useCallback(
    (id) => {
      const batch = batches.find((row) => row.batch_id === id);
      return batch ? formatBatchLabel(batch) : `Batch #${id}`;
    },
    [batches]
  );

  const handleFetchBadges = useCallback(async () => {
    if (effectiveBatchId === "") return;
    setBusy(true);
    setErrorMessage("");
    setMergeNote("");
    setBadges([]);
    setSelected(new Set());
    try {
      if (effectiveBatchId === ALL_CLASSES) {
        const result = await fetchBadgesForBatches(allClassesIds, authToken);
        setBadges(result.badges);
        setSelected(new Set(result.badges.map((row) => row.user_id)));

        // A short sheet that does not say it is short is the worst outcome
        // here — it prints, it looks finished, and one class walks away with
        // no cards. Any failure is stated in the same red alert as an outright
        // error, names the classes that are missing, and carries the server's
        // own first message.
        if (result.failures.length > 0) {
          const named = result.failures
            .map((failure) => labelForBatchId(failure.batchId))
            .join("; ");
          setErrorMessage(
            result.fetchedCount === 0
              ? `None of the ${result.requestedCount} ${filters.day} classes could be fetched, so there is no sheet. ${result.failures[0].message}`
              : `This sheet is incomplete. ${result.failures.length} of ${result.requestedCount} ${filters.day} classes could not be fetched, so their students are NOT on it — ${named}. ${result.failures[0].message} Fix that and fetch again before printing.`
          );
        } else if (result.badges.length === 0) {
          setErrorMessage(
            `No students are enrolled in any of those ${result.requestedCount} ${filters.day} classes.`
          );
        }

        if (result.badges.length > 0) {
          setMergeNote(
            `${result.badges.length} student${
              result.badges.length === 1 ? "" : "s"
            } from ${result.fetchedCount} of ${result.requestedCount} ${
              filters.day
            } classes${
              result.duplicates > 0
                ? `, after dropping ${result.duplicates} repeat enrolment${
                    result.duplicates === 1 ? "" : "s"
                  } — a student in two of these classes gets one card, not two`
                : " — nobody was enrolled in more than one of them"
            }. Sorted by first name, the same order a single class prints in.`
          );
        }
        return;
      }

      const rows = await fetchBadges({ batch_id: effectiveBatchId }, authToken);
      setBadges(rows);
      setSelected(new Set(rows.map((row) => row.user_id)));
      if (rows.length === 0) {
        // Badges are auto-issued for every enrolled student, so an empty
        // result always means an empty roster — never "not printed yet".
        setErrorMessage("No students are enrolled in that class.");
      }
    } catch (error) {
      setErrorMessage(
        describeBadgeError(error, "Could not fetch badges for that class.")
      );
    } finally {
      setBusy(false);
    }
  }, [
    effectiveBatchId,
    authToken,
    allClassesIds,
    filters.day,
    labelForBatchId,
  ]);

  /*
    Arriving from the Classes row action, fetch straight away. Landing on a
    named class and still having to press "Fetch badges" is the whole reason
    that entry point exists undone. Once only, hence the ref: refetching is
    the button's job from here on.

    On this first pass `batches` is usually still in flight, so
    `effectiveBatchId` is simply the linked id (unknown ids bypass the filter
    rule) — the badges call is by batch_id and does not wait on the class list.
    A 403, because the current branch does not cover that class, comes back
    through the same describeBadgeError path as any other failure.

    This never fires for an all-classes selection, and cannot be made to:
    `parseLinkedBatchId` accepts a positive integer and nothing else, so
    `deepLinked` implies a single class, and a deep link starts on NO_FILTERS
    where Day is "All" and the all-classes option is not even offered. Choosing
    "all classes" only ever arms the request — the write happens when the user
    presses the button, never on arrival.
  */
  const autoFetched = useRef(false);
  useEffect(() => {
    // `!authToken` holds the one-shot back rather than spending it: on a cold
    // load the token arrives with the rehydrated store, and firing before it
    // does is the 401 that badgesCrud's header describes.
    if (autoFetched.current || !deepLinked || !authToken) return;
    autoFetched.current = true;
    handleFetchBadges();
  }, [deepLinked, authToken, handleFetchBadges]);

  // The chosen class, stated plainly rather than as a dropdown to re-read.
  // Falls back to the bare id while the class list loads, and for a class the
  // current branch does not carry (where the fetch itself will have said so).
  const selectedBatchLabel = useMemo(() => {
    if (effectiveBatchId === "") return "";
    if (effectiveBatchId === ALL_CLASSES) return allClassesLabel;
    const batch = batches.find((row) => row.batch_id === effectiveBatchId);
    if (!batch) return `Batch #${effectiveBatchId}`;
    return `${formatBatchLabel(batch)}${
      batch.active_value === "Active" ? "" : "  (inactive)"
    }`;
  }, [batches, effectiveBatchId, allClassesLabel]);

  const loading = busy || states.isLoading;
  const visibleError = errorMessage || states.errorMessage;

  return (
    <div className="card card-custom zr-check-in-card">
      <div className="card-header border-0 py-5 zr-no-print">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label font-weight-bolder text-dark">
            Print Student Badges
          </span>
          <span className="text-muted mt-3 font-weight-bold font-size-sm">
            One class — or a whole day of them — on a sheet of business-card
            stock.
          </span>
        </h3>
        <div className="card-toolbar">
          <Button
            className="btn btn-light-primary font-weight-bolder font-size-sm mr-2"
            disabled={loading || effectiveBatchId === "" || !authToken}
            onClick={handleFetchBadges}
          >
            {/* The button says the scale of what it is about to do, so the
                press is never a surprise even if the panel below went
                unread. */}
            {busy
              ? "Loading…"
              : allClassesArmed
              ? `Fetch badges for ${allClassesIds.length} classes`
              : "Fetch badges"}
          </Button>
          <Button
            className="btn btn-primary font-weight-bolder font-size-sm"
            disabled={selected.size === 0}
            onClick={() => window.print()}
          >
            Print sheet
          </Button>
        </div>
      </div>

      {/* `py-0` stays. The printed sheet is this card's body, and the print
          rules zero the card's own padding but not the body's — see
          badge-sheet.css. The tab strip brings its own top margin instead. */}
      <div className="card-body py-0">
        <CheckInTabs />

        {deepLinked && (
          <div className="d-flex align-items-center flex-wrap mb-5 zr-no-print">
            <span className="text-muted font-weight-bold font-size-sm mr-3">
              Class
            </span>
            <span className="font-weight-bolder text-dark mr-4">
              {selectedBatchLabel || "—"}
            </span>
            <Button
              variant="link"
              className="btn btn-link font-weight-bold font-size-sm p-0"
              aria-expanded={pickerOpen}
              aria-controls="zr-badge-class-picker"
              onClick={() => setPickerOpen((open) => !open)}
            >
              {pickerOpen ? "Hide class picker" : "Choose a different class"}
            </Button>
          </div>
        )}

        <Collapse in={pickerOpen}>
          <div id="zr-badge-class-picker" className="zr-no-print">
            <BadgeFilters
              filters={filters}
              setFilters={setFilters}
              options={filterOptions}
              disabled={batches.length === 0}
            />

            {/*
              The class picker takes the filter bar's idiom, not the form's:
              caption under the control, no `<label>` above it. It is the last
              column of the same bar — it narrows the branch's classes to one
              exactly as the four selects above narrow them to a few, it is
              applied on change with nothing to submit, and splitting one
              continuous row of controls across two idioms would be worse than
              either idiom on its own. It gets `col-lg-4` rather than the bar's
              `col-lg-2` only because a class label is "Mon/Wed · 4:30 PM–6:00
              PM · ROB101" and has to be readable; `BatchFilter` widens its own
              stream picker to `col-lg-3` for the same reason.
            */}
            <div className="form form-label-right zr-no-print">
              <div className="form-group row">
                <div className="col-lg-4">
                  <select
                    className="form-control"
                    name="batchFilter"
                    aria-label="Select the class to print"
                    disabled={batchOptions.length === 0}
                    value={effectiveBatchId}
                    onChange={(event) => {
                      const { value } = event.target;
                      // The sentinel is kept as a string; anything else is a
                      // batch id and becomes a number, as before. Selecting
                      // the whole day only arms the request — nothing is
                      // fetched, and so nothing is minted, until the button.
                      if (value === "" || value === ALL_CLASSES) {
                        setBatchId(value);
                        return;
                      }
                      setBatchId(Number(value));
                    }}
                  >
                    <option value="">
                      {!BRANCH.branchId || BRANCH.branchId === -1
                        ? "Pick a branch in the top bar…"
                        : batches.length === 0
                        ? loading
                          ? "Loading classes…"
                          : "No classes at this branch"
                        : batchOptions.length === 0
                        ? "No classes match the filters…"
                        : "Choose a class…"}
                    </option>
                    {allClassesOffered && (
                      <option value={ALL_CLASSES}>{allClassesLabel}</option>
                    )}
                    {batchOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-text text-muted">
                    <b>Select</b> the class to print
                  </small>
                </div>

                {/*
                  Fallback path, kept inside the collapsed picker: if the class
                  list is empty or the label is unrecognisable, staff can type
                  the batch id from the Classes page URL directly. An escape
                  hatch, not a primary affordance.
                */}
                <div className="col-lg-2">
                  <input
                    type="number"
                    min={1}
                    className="form-control"
                    name="manualBatchId"
                    aria-label="Or enter a Batch ID"
                    // Blank while the whole day is selected: the sentinel is
                    // not a batch id and must never be typed into a number
                    // field, where it would read back as NaN.
                    value={typeof batchId === "number" ? batchId : ""}
                    onChange={(event) =>
                      setBatchId(
                        event.target.value === ""
                          ? ""
                          : Number(event.target.value)
                      )
                    }
                  />
                  <small className="form-text text-muted">
                    <b>Or</b> enter a Batch ID — only if the class you want is
                    not in the list.
                  </small>
                </div>
              </div>
            </div>
          </div>
        </Collapse>

        {/*
          Fetching badges is a WRITE: POST /badges mints a durable code for
          every student who does not already have a live one. For one class
          that is a handful of rows; for a whole day it is every class in it.
          So the selection arms the request and this panel states the cost in
          full — how many classes, how many students, that codes get issued,
          and that nothing has happened yet — before the button is pressed.
        */}
        {allClassesArmed && allClassesPreview && (
          <div
            className="mb-5 alert alert-custom alert-light-warning zr-no-print"
            role="status"
          >
            <div className="alert-text">
              {/*
                Three things only: how big, what cannot be undone, and that it
                has not happened yet. An earlier version also explained the
                request-per-class approach, the enrolment arithmetic behind the
                de-duplication, and that the count is an estimate — all true,
                none of it a branch admin's problem. "or more" carries the
                undercount without explaining where it comes from.
              */}
              <b>Nothing has been fetched yet.</b> Printing all{" "}
              {allClassesIds.length} {filters.day} classes covers{" "}
              {allClassesPreview.students}
              {allClassesPreview.classesWithNoRoster > 0 ? " or more" : ""}{" "}
              student{allClassesPreview.students === 1 ? "" : "s"}
              {allClassesPreview.duplicates > 0
                ? " — a student in two of these classes still gets one card"
                : ""}
              . Fetching <b>issues a permanent badge code</b> to anyone who does
              not already hold one.
            </div>
          </div>
        )}

        {visibleError && (
          <div
            className="mb-5 alert alert-custom alert-light-danger zr-no-print"
            role="alert"
          >
            <div className="alert-text">{visibleError}</div>
          </div>
        )}

        {badges.length > 0 ? (
          <>
            <div className="d-flex align-items-center flex-wrap mb-5 zr-no-print">
              <Button
                className="btn btn-light font-weight-bolder font-size-sm mr-2"
                disabled={selected.size === badges.length}
                onClick={() =>
                  setSelected(new Set(badges.map((badge) => badge.user_id)))
                }
              >
                All
              </Button>
              <Button
                className="btn btn-light font-weight-bolder font-size-sm mr-4"
                disabled={selected.size === 0}
                onClick={() => setSelected(new Set())}
              >
                None
              </Button>
              <span className="text-muted font-weight-bold font-size-sm">
                {selected.size} of {badges.length} selected
              </span>
            </div>

            {/* What this merged sheet is, after the fact: which classes it
                covers and how many repeat enrolments were dropped. */}
            {mergeNote && (
              <p className="text-muted font-size-sm zr-no-print">{mergeNote}</p>
            )}

            <p className="text-muted font-size-sm zr-no-print">
              Every student starts ticked — click a card to leave it off the
              sheet. Print at 100% scale — turn off “fit to page”. Card stock or
              lamination roughly triples how long a badge survives a schoolbag.
            </p>

            <BadgeSheet>
              {badges.map((badge) => (
                <BadgeCard
                  key={badge.badge_code}
                  badge={badge}
                  selected={selected.has(badge.user_id)}
                  printSkipped={
                    soloPrintId !== null
                      ? badge.user_id !== soloPrintId
                      : !selected.has(badge.user_id)
                  }
                  onToggle={() => toggleSelected(badge.user_id)}
                  onPrintOne={() => setSoloPrintId(badge.user_id)}
                />
              ))}
            </BadgeSheet>
          </>
        ) : (
          <p className="text-muted text-center py-10 zr-no-print">
            Pick a class and fetch badges to build a print sheet.
          </p>
        )}
      </div>
    </div>
  );
}
